import { Trans } from '@/components/Trans'
import {
  DAILY_PURCHASE_OPTIONS,
  DURATION,
  type Duration,
  MONTHLY_PURCHASE_OPTIONS,
  PurchaseOption,
} from '@/consts/order'
import { cn } from '@/utils/style'

export type RenewPurchaseValue = number | 'custom'

interface RenewPurchaseForSectionProps {
  duration: Duration
  purchaseFor: RenewPurchaseValue
  subscriptionTotalDays?: number
  onChange: (payload: { purchaseFor: RenewPurchaseValue; totalDays: number }) => void
  className?: string
}

export function RenewPurchaseForSection({
  duration,
  purchaseFor,
  subscriptionTotalDays,
  onChange,
  className,
}: RenewPurchaseForSectionProps) {
  const isDaily = duration === DURATION.DAILY
  const options = isDaily ? DAILY_PURCHASE_OPTIONS : MONTHLY_PURCHASE_OPTIONS

  const fixedOptions = options.filter((o) => o.value !== 'custom')
  const customOption = options.find((o) => o.value === 'custom')

  const handleFixedClick = (opt: PurchaseOption) => {
    if (opt.value === 'custom') {
      const defaultDays = isDaily ? 1 : 30
      onChange({ purchaseFor: 'custom', totalDays: defaultDays })
      return
    }
    onChange({ purchaseFor: opt.value, totalDays: opt.value as number })
  }

  const handleCustomChange = (value: string) => {
    if (value === '') {
      onChange({ purchaseFor: 'custom', totalDays: 0 })
      return
    }

    const num = Number(value)
    if (Number.isNaN(num)) return

    const max = isDaily ? 180 : 6
    const clamped = Math.min(Math.max(num, 0), max)
    const totalDays = isDaily ? clamped : clamped * 30

    onChange({ purchaseFor: 'custom', totalDays })
  }

  const customDisplayValue = (() => {
    if (!subscriptionTotalDays || subscriptionTotalDays <= 0) return ''
    return isDaily ? subscriptionTotalDays : Math.max(1, Math.round(subscriptionTotalDays / 30))
  })()

  const isCustomSelected = purchaseFor === 'custom'

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="t-sb text-secondary">
        <Trans i18nKey="create.purchaseFor">Purchase for</Trans>
      </div>

      <div className="grid grid-cols-5 gap-3">
        {fixedOptions.map((opt) => {
          const isSelected = purchaseFor === opt.value
          const displayNumber = isDaily
            ? (opt.value as number)
            : Math.round((opt.value as number) / 30)

          const isSingle = isDaily ? (opt.value as number) === 1 : displayNumber === 1

          return (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'relative h-11 px-4 rounded-xl border text-sm flex items-center justify-center',
                isSelected
                  ? 'border-secondary bg-highlight/10 text-primary font-semibold'
                  : 'border-neutral-200 bg-panel-white text-secondary hover:bg-neutral-50',
              )}
              onClick={() => handleFixedClick(opt)}
            >
              {opt.discount && (
                <div
                  className="absolute -top-3 right-2 px-2 py-1 rounded-[4px] text-[11px] leading-[14px] text-white flex items-center justify-center"
                  style={{
                    background: isSelected
                      ? 'radial-gradient(114.58% 304.75% at 2.72% -20%, #C0D5FF 0%, #335CFF 100%)'
                      : 'radial-gradient(#CACFD8, #CACFD8)',
                  }}
                >
                  {opt.discount}
                </div>
              )}

              <span className="t-sb">
                {displayNumber}{' '}
                <Trans
                  i18nKey={
                    isDaily
                      ? isSingle
                        ? 'create.PurchaseForSection.day'
                        : 'create.PurchaseForSection.days'
                      : isSingle
                        ? 'create.PurchaseForSection.month'
                        : 'create.PurchaseForSection.months'
                  }
                >
                  {isDaily ? (isSingle ? 'Day' : 'Days') : isSingle ? 'Month' : 'Months'}
                </Trans>
              </span>
            </button>
          )
        })}
      </div>

      <div className="w-full">
        {!isCustomSelected ? (
          <button
            type="button"
            className={cn(
              'relative h-11 w-full rounded-xl border text-sm flex items-center justify-between px-4',
              'border-neutral-200 bg-panel-white text-secondary hover:bg-neutral-50',
            )}
            onClick={() => {
              const defaultDays = isDaily ? 1 : 30
              onChange({ purchaseFor: 'custom', totalDays: defaultDays })
            }}
          >
            <span className="t-sb">
              <Trans i18nKey="create.PurchaseForSection.custom">Custom</Trans>
            </span>
            <span className="t-xs text-tertiary">
              {isDaily ? (
                <Trans i18nKey="create.PurchaseForSection.days">Days</Trans>
              ) : (
                <Trans i18nKey="create.PurchaseForSection.months">Months</Trans>
              )}
            </span>

            {customOption?.discount && (
              <div
                className="absolute -top-3 right-4 px-2 py-1 rounded-[4px] text-[11px] leading-[14px] text-white flex items-center justify-center"
                style={{
                  background: 'radial-gradient(#CACFD8, #CACFD8)',
                }}
              >
                {customOption.discount}
              </div>
            )}
          </button>
        ) : (
          <div
            className={cn(
              'relative h-11 w-full rounded-xl border text-sm flex items-center justify-between px-4',
              'border-secondary bg-highlight/10 text-highlight font-semibold',
            )}
          >
            <span className="t-sb">
              <Trans i18nKey="create.PurchaseForSection.custom">Custom</Trans>
            </span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="w-[80px] h-8 rounded-md border border-neutral-200 bg-panel-white px-2 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-secondary"
                min={1}
                max={isDaily ? 180 : 6}
                value={customDisplayValue}
                onChange={(e) => handleCustomChange(e.target.value)}
                onBlur={() => {
                  if (!subscriptionTotalDays || subscriptionTotalDays <= 0) {
                    const defaultDays = isDaily ? 1 : 1
                    onChange({
                      purchaseFor: 'custom',
                      totalDays: isDaily ? defaultDays : defaultDays * 30,
                    })
                  }
                }}
              />
              <span className="t-xs text-secondary">
                {isDaily ? (
                  <Trans i18nKey="create.PurchaseForSection.days">Days</Trans>
                ) : (
                  <Trans i18nKey="create.PurchaseForSection.months">Months</Trans>
                )}
              </span>
            </div>

            {customOption?.discount && (
              <div
                className="absolute -top-3 right-4 px-2 py-1 rounded-[4px] text-[11px] leading-[14px] text-white flex items-center justify-center"
                style={{
                  background:
                    'radial-gradient(114.58% 304.75% at 2.72% -20%, #C0D5FF 0%, #335CFF 100%)',
                }}
              >
                {customOption.discount}
              </div>
            )}
          </div>
        )}
      </div>

      {isDaily ? (
        <div className="t-xs text-tertiary mt-1">
          <Trans i18nKey="create.customPeriodMustBeBetweenDays">
            The custom period must be between 1 and 180 days.
          </Trans>
        </div>
      ) : (
        <div className="t-xs text-tertiary mt-1">
          <Trans i18nKey="create.customPeriodMustBeBetweenMonths">
            The custom period must be between 1 and 6 months.
          </Trans>
        </div>
      )}
    </div>
  )
}
